import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface Usuario {
    id: number;
    nombre: string;
    apellido: string;
    telefono: string;
    email: string;
    role?: number;
    consorcios?: any[];
}

export interface LoginResponse {
    success: boolean;
    message: string;
    data?: Usuario;
}

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private apiUrl = environment.apiUrl;
    private currentUserSubject: BehaviorSubject<Usuario | null>;
    public currentUser: Observable<Usuario | null>;

    private selectedConsorcioSubject: BehaviorSubject<any | null>;
    public selectedConsorcio: Observable<any | null>;

    constructor(private http: HttpClient) {
        // Intentar cargar usuario desde localStorage al iniciar
        const storedUser = localStorage.getItem('currentUser');
        this.currentUserSubject = new BehaviorSubject<Usuario | null>(
            storedUser ? JSON.parse(storedUser) : null
        );
        this.currentUser = this.currentUserSubject.asObservable();

        // Intentar cargar consorcio seleccionado
        const storedConsorcio = localStorage.getItem('selectedConsorcio');
        this.selectedConsorcioSubject = new BehaviorSubject<any | null>(
            storedConsorcio ? JSON.parse(storedConsorcio) : null
        );
        this.selectedConsorcio = this.selectedConsorcioSubject.asObservable();
    }

    /**
     * Obtiene el valor actual del usuario sin suscribirse
     */
    public get currentUserValue(): Usuario | null {
        return this.currentUserSubject.value;
    }

    /**
     * Verifica si el usuario está autenticado
     */
    public isAuthenticated(): boolean {
        return this.currentUserValue !== null;
    }

    /**
     * Obtiene el usuario actual
     */
    public getUser(): Usuario | null {
        return this.currentUserValue;
    }

    /**
     * Realiza el login del usuario
     * @param email Email del usuario
     * @param password Contraseña del usuario
     */
    login(email: string, password: string): Observable<LoginResponse> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json'
        });

        return this.http.post<LoginResponse>(
            `${this.apiUrl}/usuarios/login`,
            { email, password },
            { headers, withCredentials: true }
        ).pipe(
            tap(response => {
                // Si el login fue exitoso, guardar usuario
                if (response.success && response.data) {
                    localStorage.setItem('currentUser', JSON.stringify(response.data));
                    this.currentUserSubject.next(response.data);

                    // Manejo de Consorcio Seleccionado
                    if (response.data.consorcios && response.data.consorcios.length > 0) {
                        const currentSelection = this.selectedConsorcioSubject.value;
                        const isSelectionValid = currentSelection && response.data.consorcios.find(c => c.id === currentSelection.id);

                        if (!isSelectionValid) {
                            // Si no hay selección o no es válida para este usuario, seleccionar el primero
                            this.setSelectedConsorcio(response.data.consorcios[0]);
                        } else {
                            // Re-validar/Actualizar la selección actual con los datos frescos
                            const freshSelection = response.data.consorcios.find(c => c.id === currentSelection.id);
                            this.setSelectedConsorcio(freshSelection);
                        }
                    } else {
                        this.setSelectedConsorcio(null);
                    }
                }
            }),
            catchError(error => {
                console.error('Error en login:', error);
                return throwError(() => error);
            })
        );
    }

    /**
     * Verifica la sesión actual con el servidor
     */
    checkAuth(): Observable<LoginResponse> {
        return this.http.get<LoginResponse>(
            `${this.apiUrl}/usuarios/me`,
            { withCredentials: true }
        ).pipe(
            tap(response => {
                if (response.success && response.data) {
                    localStorage.setItem('currentUser', JSON.stringify(response.data));
                    this.currentUserSubject.next(response.data);

                    // Re-validar consorcio seleccionado
                    const currentSelection = this.selectedConsorcioSubject.value;
                    if (currentSelection && response.data.consorcios) {
                        const freshSelection = response.data.consorcios.find(c => c.id === currentSelection.id);
                        if (freshSelection) {
                            this.setSelectedConsorcio(freshSelection);
                        } else if (response.data.consorcios.length > 0) {
                            this.setSelectedConsorcio(response.data.consorcios[0]);
                        } else {
                            this.setSelectedConsorcio(null);
                        }
                    } else if (response.data.consorcios && response.data.consorcios.length > 0) {
                        this.setSelectedConsorcio(response.data.consorcios[0]);
                    }
                }
            }),
            catchError(error => {
                // Si falla (ej. 401), limpiar estado local
                localStorage.removeItem('currentUser');
                localStorage.removeItem('selectedConsorcio');
                this.currentUserSubject.next(null);
                this.selectedConsorcioSubject.next(null);
                return throwError(() => error);
            })
        );
    }

    /**
     * Cierra sesión del usuario
     */
    /**
     * Obtiene el consorcio seleccionado actualmente
     */
    getSelectedConsorcio(): any | null {
        return this.selectedConsorcioSubject.value;
    }

    /**
     * Establece el consorcio actual
     * @param consorcio Objeto consorcio
     */
    setSelectedConsorcio(consorcio: any) {
        if (consorcio) {
            localStorage.setItem('selectedConsorcio', JSON.stringify(consorcio));
        } else {
            localStorage.removeItem('selectedConsorcio');
        }
        this.selectedConsorcioSubject.next(consorcio);
    }

    /**
     * Cierra sesión del usuario
     */
    logout(): Observable<any> {
        return this.http.post(
            `${this.apiUrl}/usuarios/logout`,
            {},
            { withCredentials: true }
        ).pipe(
            tap(() => {
                // Limpiar datos locales
                localStorage.removeItem('currentUser');
                localStorage.removeItem('selectedConsorcio');
                this.currentUserSubject.next(null);
                this.selectedConsorcioSubject.next(null);
            }),
            catchError(error => {
                // Aunque falle el logout en el servidor, limpiar localmente
                localStorage.removeItem('currentUser');
                localStorage.removeItem('selectedConsorcio');
                this.currentUserSubject.next(null);
                this.selectedConsorcioSubject.next(null);
                return throwError(() => error);
            })
        );
    }
    /**
     * Recupera la contraseña por email
     * @param email Email del usuario
     */
    recoverPassword(email: string): Observable<any> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json'
        });

        return this.http.post(
            `${this.apiUrl}/usuarios/recover`,
            { email },
            { headers, withCredentials: true }
        );
    }
}
